/**
 * Validation utility functions
 */

import { getMaxFileSize } from './configUtils';

/**
 * Checks if a string contains only numeric characters
 * @param val - The string value to check
 * @returns True if the string is numeric, false otherwise
 */
export function isNumeric(val: string): boolean {
  return /^\d+$/.test(val?.trim() || '');
}

/**
 * Checks if a string is not empty after trimming whitespace
 * @param val - The string value to check
 * @returns True if the string has content, false otherwise
 */
export function hasContent(val: string): boolean {
  return !!(val && val.trim());
}

export const MIN_DRAW_NAME_LENGTH = 3;

export function isValidDrawName(val: string): boolean {
  return hasContent(val) && val.trim().length >= MIN_DRAW_NAME_LENGTH;
}

/**
 * Validates email format
 * @param email - The email address to validate
 * @returns True if the email is valid, false otherwise
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email?.trim() || '');
}

// ==================== File Validation Functions ====================

/**
 * File validation result interface
 */
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Error messages for file validation
 */
export const FILE_VALIDATION_ERRORS = {
  INVALID_FILE_TYPE: "Invalid file type. Only CSV, TXT, and TSV files are allowed",
  FILE_TOO_LARGE: "File is too large",
  EMPTY_FILE: "File is empty"
};

/**
 * Validate file extension
 * @param fileName - The name of the file to validate
 * @returns ValidationResult with validation status and error message if invalid
 */
export function validateFileExtension(fileName: string): ValidationResult {
  const allowedExtensions = ['.csv', '.txt', '.tsv'];
  const fileExtension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  
  if (!allowedExtensions.includes(fileExtension)) {
    return {
      valid: false,
      error: `${FILE_VALIDATION_ERRORS.INVALID_FILE_TYPE}. Found: ${fileExtension}`
    };
  }
  
  return { valid: true };
}

/**
 * Validate file size
 * @param fileSize - The size of the file in bytes
 * @param maxFileSize - Maximum allowed file size in bytes (optional, defaults to config value)
 * @returns ValidationResult with validation status and error message if invalid
 */
export function validateFileSize(fileSize: number, maxFileSize?: number): ValidationResult {
  if (fileSize <= 0) {
    return {
      valid: false,
      error: FILE_VALIDATION_ERRORS.EMPTY_FILE
    };
  }
  
  const maxSize = maxFileSize ?? getMaxFileSize();
  if (fileSize > maxSize) {
    const fileSizeMB = (fileSize / (1024 * 1024)).toFixed(1);
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
    return {
      valid: false,
      error: `${FILE_VALIDATION_ERRORS.FILE_TOO_LARGE} (${fileSizeMB}MB). Maximum allowed: ${maxSizeMB}MB`
    };
  }
  
  return { valid: true };
}

/**
 * Validate file is not empty
 * @param fileSize - The size of the file in bytes
 * @returns ValidationResult with validation status and error message if invalid
 */
export function validateFileNotEmpty(fileSize: number): ValidationResult {
  if (fileSize === 0) {
    return {
      valid: false,
      error: FILE_VALIDATION_ERRORS.EMPTY_FILE
    };
  }
  
  return { valid: true };
}

/**
 * Comprehensive file validation
 * @param file - The File object to validate
 * @param maxFileSize - Maximum allowed file size in bytes (optional)
 * @returns ValidationResult with validation status and error message if invalid
 */
export function validateFile(file: File, maxFileSize?: number): ValidationResult {
  // Check file extension
  const extValidation = validateFileExtension(file.name);
  if (!extValidation.valid) {
    return extValidation;
  }
  
  // Check file size
  const sizeValidation = validateFileSize(file.size, maxFileSize);
  if (!sizeValidation.valid) {
    return sizeValidation;
  }
  
  // Check file is not empty
  const emptyValidation = validateFileNotEmpty(file.size);
  if (!emptyValidation.valid) {
    return emptyValidation;
  }
  
  return { valid: true };
}
